import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiBlob, saveBlob } from '../../api/http';
import { Notice, errorMessage, secondaryButton } from '../../components/common';
import { t } from '../../i18n';
import { formatMinor } from '../../lib/money';

interface Statement {
  currency: string;
  totalMinor: number;
  paidMinor: number;
  balanceMinor: number;
  overdueMinor: number;
  invoices: {
    id: string;
    invoiceNumber: string;
    issueDate: string;
    status: string;
    totalMinor: number;
    balanceMinor: number;
    overdueMinor: number;
  }[];
  payments: {
    id: string;
    receiptNumber: string;
    paidOn: string;
    amountMinor: number;
    method: string;
    status: string;
  }[];
}

/** Fee statement of one child with receipt downloads. Money arrives as integer minor units. */
export function ChildFees({ studentId }: { studentId: string }) {
  const [message, setMessage] = useState<string | null>(null);
  const statement = useQuery({
    queryKey: ['me', 'children', studentId, 'fees'],
    queryFn: () => api<Statement>(`/me/children/${studentId}/fees`),
  });

  if (statement.isPending) return <p role="status">{t('common.loading')}</p>;
  if (statement.isError) return <Notice kind="error">{errorMessage(statement.error)}</Notice>;
  const s = statement.data;

  const download = async (id: string, number: string) => {
    try {
      saveBlob(
        await apiBlob(`/me/children/${studentId}/fees/receipts/${id}/receipt.pdf`),
        `receipt-${number}.pdf`,
      );
    } catch (e) {
      setMessage(errorMessage(e));
    }
  };

  return (
    <section aria-label={t('child.tab.fees')} className="space-y-4">
      {message && <Notice kind="error">{message}</Notice>}
      <p className="text-3xl font-bold">
        {formatMinor(s.balanceMinor, s.currency)}
        <span className="ml-2 text-sm font-normal text-slate-600">{t('child.fees.balance')}</span>
      </p>
      {s.overdueMinor > 0 && (
        <p className="text-sm text-red-700">
          {t('fees.overdue')}: {formatMinor(s.overdueMinor, s.currency)}
        </p>
      )}
      {s.invoices.length === 0 && <p className="text-slate-600">{t('child.fees.none')}</p>}
      <ul className="space-y-2 text-sm">
        {s.invoices.map((i) => (
          <li key={i.id} className="rounded-lg border border-slate-200 bg-white p-3">
            <span className="font-mono text-xs">{i.invoiceNumber}</span> · {i.issueDate} ·{' '}
            {t('fees.total')} {formatMinor(i.totalMinor, s.currency)} · {t('fees.balance')}{' '}
            {formatMinor(i.balanceMinor, s.currency)}
            {i.status === 'cancelled' ? ` (${t('fees.statusCancelled')})` : ''}
          </li>
        ))}
      </ul>
      {s.payments.length > 0 && <h3 className="font-semibold">{t('fees.receipts')}</h3>}
      <ul className="space-y-2 text-sm">
        {s.payments.map((p) => (
          <li
            key={p.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white p-3"
          >
            <span>
              <span className="font-mono text-xs">{p.receiptNumber}</span> · {p.paidOn} ·{' '}
              {formatMinor(p.amountMinor, s.currency)}
              {p.status === 'void' ? ` (${t('fees.statusVoid')})` : ''}
            </span>
            <button
              type="button"
              className={secondaryButton}
              aria-label={t('fees.receiptPdf', { number: p.receiptNumber })}
              onClick={() => void download(p.id, p.receiptNumber)}
            >
              PDF
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
